import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { SNOMED_USER_ROLES } from '@/utils'
import { logError } from '@/utils/errorUtils'

/**
 * Whether the signed-in user sees the CRM (PD-1993): for now only super admins and care
 * coordinators (docs/CRM.md). The middleware enforces the same line on every CRM call; this only
 * hides the page. `undefined` until the role is known.
 */
export function useCrmAccess(): boolean | undefined {
  const medplum = useMedplum()
  const superAdmin = medplum.isSuperAdmin()
  const coordinator = useIsCareCoordinator(!superAdmin)
  return superAdmin || coordinator
}

/**
 * Whether the signed-in practitioner holds an active care-coordinator role on any row, as the
 * middleware reads it. Only looked up when `enabled`; false when the lookup fails.
 */
function useIsCareCoordinator(enabled: boolean): boolean | undefined {
  const medplum = useMedplum()
  const profile = medplum.getProfile()
  const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined
  const [coordinator, setCoordinator] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !practitionerId) {
      setCoordinator(false)
      return
    }
    let cancelled = false
    setCoordinator(undefined)
    medplum
      .searchResources('PractitionerRole', `practitioner=Practitioner/${practitionerId}`)
      .then((roles) => {
        if (cancelled) return
        setCoordinator(
          roles.some(
            (role) =>
              role.active !== false &&
              role.code?.some((code) =>
                code.coding?.some(
                  (coding) => coding.code === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code,
                ),
              ),
          ),
        )
      })
      .catch((error: unknown) => {
        logError('crm-access', 'Failed to read the practitioner roles', error)
        if (!cancelled) setCoordinator(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, medplum, practitionerId])

  return coordinator
}
